import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BookingPurchaseDialogData } from './BookingPurchaseDialog.types';
import type { PaymentOption } from './payment-section/PaymentSection';
import {
  CARD,
  paymentOptions,
  shouldShowStartDate,
} from './buyMembershipRules';
import { buyMembershipDialogData } from './buyMembershipProjection';
import type { BuyMembershipHostData } from './buyMembership.types';
import { useBuyMembershipPricing } from './useBuyMembershipPricing';
import { useBuyMembershipPurchase } from './useBuyMembershipPurchase';
import { t } from '../../services/locale';

function preferredPayment(
  options: PaymentOption[],
  current?: PaymentOption
): PaymentOption | undefined {
  const matching = options.find((option) => option.key === current?.key);
  if (matching) return matching;
  const card = options.find((option) => option.key === CARD);
  return card || options[0];
}

function useSelectedPayment(options: PaymentOption[]) {
  const [selectedPayment, setSelectedPayment] = useState<PaymentOption | undefined>(
    preferredPayment(options)
  );
  useEffect(() => {
    setSelectedPayment((current) => preferredPayment(options, current));
  }, [options]);
  return { selectedPayment, setSelectedPayment };
}

function useInitialMembershipEffects(data: BuyMembershipHostData) {
  useEffect(() => {
    data.trackIntent();
    data.trackEvent({
      category: 'source:membership',
      name: data.user?._id ? 'SHOW_BUY_MODAL_LOGGED_IN' : 'SHOW_BUY_MODAL_LOGGED_OUT',
    });
    data.scrollIframeToTop();
  }, []);
}

function useMembershipPriceQuote(
  data: BuyMembershipHostData,
  refreshPrice: () => Promise<void>
) {
  useEffect(() => {
    void refreshPrice();
  }, [data.user?._id, data.user?.membership?.status]);
}

function useMembershipEligibility(data: BuyMembershipHostData) {
  const [memberAllowed, setMemberAllowed] = useState(true);
  const [notAllowedReason, setNotAllowedReason] = useState('');
  useEffect(() => {
    let cancelled = false;
    setMemberAllowed(true);
    setNotAllowedReason('');
    const request = eligibilityRequest(data);
    if (!request) {
      return;
    }
    void data.canBeBoughtByMember(request.membershipId, request.userId).then((result) => {
      applyEligibilityResult({
        data,
        result,
        cancelled,
        setMemberAllowed,
        setNotAllowedReason,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [
    data.canBeBoughtByMember,
    data.membership._id,
    data.membership.buy_just_once,
    data.user?._id,
  ]);
  return { memberAllowed, notAllowedReason };
}

function eligibilityRequest(data: BuyMembershipHostData) {
  if (!data.user?._id) return null;
  if (!data.membership._id) return null;
  if (!data.membership.buy_just_once) return null;
  return { userId: data.user._id, membershipId: data.membership._id };
}

function applyEligibilityResult(args: {
  data: BuyMembershipHostData;
  result: { success?: boolean; message?: string } | void;
  cancelled: boolean;
  setMemberAllowed: (allowed: boolean) => void;
  setNotAllowedReason: (reason: string) => void;
}) {
  if (!args.result) return;
  if (args.cancelled) return;
  args.setMemberAllowed(args.result.success === true);
  args.setNotAllowedReason(
    args.result.message ? t(args.result.message) : ''
  );
}

function useDialogState(data: BuyMembershipHostData, options: PaymentOption[]) {
  const payment = useSelectedPayment(options);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<string | undefined>(
    shouldShowStartDate(data, false) ? data.todayLocal() : undefined
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [autorenewalAccepted, setAutorenewalAccepted] = useState(false);
  const clearStartDateForProRated = useCallback(
    () => setSelectedStartDate(undefined),
    []
  );
  return {
    ...payment,
    paymentDropdownOpen,
    setPaymentDropdownOpen,
    selectedStartDate,
    setSelectedStartDate,
    termsAccepted,
    setTermsAccepted,
    autorenewalAccepted,
    setAutorenewalAccepted,
    clearStartDateForProRated,
  };
}

function useShareMembership(data: BuyMembershipHostData) {
  const [shared, setShared] = useState(false);
  const shareMembership = useCallback(() => {
    data.copyShareUrl();
    setShared(true);
    window.setTimeout(() => setShared(false), 3000);
  }, [data]);
  return { shared, shareMembership };
}

export function useBuyMembershipDialog(
  data: BuyMembershipHostData
): BookingPurchaseDialogData {
  const options = useMemo(
    () => paymentOptions(data),
    [data, data.branch, data.user]
  );
  const dialogState = useDialogState(data, options);
  const share = useShareMembership(data);
  const eligibility = useMembershipEligibility(data);
  const pricing = useBuyMembershipPricing(
    data,
    dialogState.selectedStartDate,
    dialogState.clearStartDateForProRated
  );
  const purchase = useBuyMembershipPurchase({
    data,
    ...dialogState,
    ...pricing,
  });

  useInitialMembershipEffects(data);
  useMembershipPriceQuote(data, pricing.refreshPrice);

  useEffect(() => {
    if (!eligibility.memberAllowed) {
      purchase.setView('error');
    }
  }, [eligibility.memberAllowed]);

  return buyMembershipDialogData({
    data,
    options,
    ...dialogState,
    ...share,
    ...eligibility,
    ...pricing,
    ...purchase,
  });
}
